import { useState } from 'react';

import CopyIcon from '@/assets/svg/ui-elements/copy-filled-current.svg';
import { getSuiExplorerTransactionUrl } from '@/config/sui';
import { Tooltip } from '@/design-system';
import { useCopy } from '@/global-hooks/use-copy';

import {
  Digest,
  DigestActionLink,
  DigestActions,
  DigestCopyButton,
  DigestRow,
  DigestSection,
  DigestValue,
  ExternalIcon,
  SectionLabel,
} from './transaction-detail-modal.styles';

interface TransactionDetailDigestProps {
  hash: string;
}

export const TransactionDetailDigest = ({ hash }: TransactionDetailDigestProps) => {
  const [copyFailed, setCopyFailed] = useState(false);
  const { isTooltipOpen: copied, handleClick: copy } = useCopy({ textToCopy: hash });
  const displayHash = hash.length > 13 ? `${hash.slice(0, 8)}…${hash.slice(-5)}` : hash;

  const copyDigest = async () => {
    setCopyFailed(false);
    try {
      await copy();
    } catch {
      setCopyFailed(true);
    }
  };

  const copyLabel = copied
    ? 'Transaction digest copied'
    : copyFailed
      ? 'Retry copying transaction digest'
      : 'Copy transaction digest';

  return (
    <DigestSection>
      <SectionLabel as="h3" variant="body4" fontWeight={600}>
        Transaction hash
      </SectionLabel>
      <DigestRow role="group" aria-label="Transaction digest">
        <DigestValue>
          <Digest variant="body3" title={hash}>
            {displayHash}
          </Digest>
          <Tooltip title="Copied" open={copied} side="top">
            <DigestCopyButton
              type="button"
              variant="outline"
              color="secondary"
              size="xs"
              aria-label={copyLabel}
              onClick={() => void copyDigest()}
            >
              <CopyIcon aria-hidden="true" />
            </DigestCopyButton>
          </Tooltip>
        </DigestValue>
        <DigestActions>
          <DigestActionLink
            href={getSuiExplorerTransactionUrl(hash)}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            aria-label="Open transaction in Sui Explorer"
          >
            View in Explorer
            <ExternalIcon width={12} height={12} aria-hidden="true" />
          </DigestActionLink>
        </DigestActions>
      </DigestRow>
    </DigestSection>
  );
};
